import { useContext, useEffect, useState } from 'react';
import Modal from '../../../modal';
import Utils from '../../../utils';
import {
	INITIAL_FORM_DATA,
	INITIAL_VALIDATIONS,
	USPS_CONSTANTS,
} from '../constants';

const { Communication, Util } = Utils;
const { ModalContext } = Modal;

const GetInitialFormData = onboardingStatus => {
	const initialData = { ...INITIAL_FORM_DATA, ...onboardingStatus };
	return initialData;
};

export const useUSPSForm = (onboardingStatus, userInfoToken) => {
	const [formData, setFormData] = useState(
		GetInitialFormData(onboardingStatus)
	);
	const [isLoading, setIsLoading] = useState(false);
	const [validations, setValidations] = useState(INITIAL_VALIDATIONS);
	const [availableCrids, setAvailableCrids] = useState([]);
	const [allMids, setAllMids] = useState([]);
	const [selectableMids, setSelectableMids] = useState([]);
	const [availableEpsAccounts, setAvailableEpsAccounts] = useState([]);
	const { showError } = useContext(ModalContext);

	useEffect(() => {
		const fetchAndSetFormOptions = async (onboardingStatus, userInfoToken) => {
			setIsLoading(true);
			var userInfoResponse = await Communication.getUspsUserInfo({
				token: userInfoToken,
			});

			if (!userInfoResponse?.data) {
				setAvailableEpsAccounts([]);
				setAvailableCrids([]);
				setSelectableMids([]);
				return;
			}

			if (userInfoResponse.data.PaymentAccounts?.Accounts) {
				const PaymentAccounts = userInfoResponse.data.PaymentAccounts.Accounts;
				setAvailableEpsAccounts([
					{ value: '', label: 'Select One' },
					...PaymentAccounts.map(acc => ({ value: acc, label: acc })),
				]);
			}
			if (userInfoResponse.data.MailOwners) {
				const allCrids = userInfoResponse.data.MailOwners.reduce(
					(_result, owner) => {
						if (owner.Crid) {
							_result.push({ value: owner.Crid, label: owner.Crid });
						}
						return _result;
					},
					[]
				);

				const allMidsReduced = userInfoResponse.data.MailOwners.reduce(
					(_result, owner) => {
						if (owner.Mids) {
							_result[owner.Crid] = [
								{ value: '', label: 'Select Mid #' },
								...owner.Mids.map(mid => ({ value: mid, label: mid })),
							];
						}
						return _result;
					},
					{}
				);
				setAllMids(allMidsReduced);

				if (allCrids.length) {
					setAvailableCrids([
						{ value: '', label: 'Select Crid #' },
						...allCrids,
					]);
				} else setAvailableCrids([]);

				if (onboardingStatus && onboardingStatus.Crid) {
					setSelectableMids(allMidsReduced[onboardingStatus.Crid] || []);
				} else if (Object.keys(allMidsReduced).length) {
					setSelectableMids([{ value: '', label: 'Select Crid #' }]);
				} else setSelectableMids([]);
			} else {
				setAvailableCrids([]);
				setSelectableMids([]);
			}
			setIsLoading(false);
		};

		if (
			userInfoToken ||
			(onboardingStatus?.IsOnboarded === true &&
				onboardingStatus?.IsAuctaneOwned === false)
		) {
			fetchAndSetFormOptions(onboardingStatus, userInfoToken);
			validateForm();
		}
	}, [onboardingStatus, userInfoToken]);

	const validateForm = () => {
		const newValidations = { ...validations };
		let isValid = true;

		const fieldsToValidate = [
			USPS_CONSTANTS.FORM_FIELDS.CRID_NUMBER,
			USPS_CONSTANTS.FORM_FIELDS.MID_NUMBER,
			USPS_CONSTANTS.FORM_FIELDS.EPS_ACCOUNT_NUMBER,
		];

		fieldsToValidate.forEach(key => {
			if (!formData[key]) {
				newValidations[key] = USPS_CONSTANTS.VALIDATION_STATES.INVALID;
				isValid = false;
			} else {
				newValidations[key] = USPS_CONSTANTS.VALIDATION_STATES.VALID;
			}
		});

		setValidations(newValidations);
		return isValid;
	};

	const onChangeSelect = ({ target: { name, value } }) => {
		setFormData(prev => ({ ...prev, [name]: value }));
		setValidations(prev => ({
			...prev,
			[name]: value
				? USPS_CONSTANTS.VALIDATION_STATES.VALID
				: USPS_CONSTANTS.VALIDATION_STATES.INVALID,
		}));
		if (name === USPS_CONSTANTS.FORM_FIELDS.CRID_NUMBER && value) {
			setSelectableMids(allMids[value] || []);
		}
	};

	const submitForm = async () => {
		if (!validateForm()) {
			return false;
		}

		setIsLoading(true);
		try {
			const getMailerIdResponse = await Communication.getMailerIDs();
			const mailerIdData =
				typeof getMailerIdResponse?.data === 'string'
					? JSON.parse(getMailerIdResponse.data)
					: getMailerIdResponse?.data;

			// Only set mailer IDs if Primary is null
			if (!mailerIdData?.Primary) {
				const mailerIdResponse = await Communication.setMailerIDs({
					Primary: formData[USPS_CONSTANTS.FORM_FIELDS.MID_NUMBER],
					Secondary: null,
					PayOnUseReturn: null,
					PayOnUseReturnMailerIdType: 0,
					HysteresisMid: null,
				});
				if (!Communication.isSuccessful(mailerIdResponse)) {
					showError({
						description: 'Failed to set Manifest/Master MID',
					});
					return false;
				}
			}

			const response = await Communication.postUspsBusinessOnboardingStatus({
				data: formData,
			});
			const result = Communication.isSuccessful(response);
			return result;
		} catch (error) {
			showError({
				description: 'Failed to connect USPS business account',
			});
			return false;
		} finally {
			setIsLoading(false);
		}
	};

	return {
		formData,
		validations,
		isLoading,
		availableCrids,
		selectableMids,
		availableEpsAccounts,
		onChangeSelect,
		submitForm,
	};
};
